import { useState, useEffect, useMemo } from 'react';
import DOMPurify from 'dompurify';
import API from '../utils/API';
import { downloadReportAsPDF } from '../utils/reportExport';
import { computeCalendarHeaders } from '../utils/billSortGrouping';
import ReportBillListItem from './ReportBillListItem';
import './reportDetailModal.css';

export default function ReportDetailModal({ reportId, teamId, canManage, onClose, onDeleted }) {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [introExpanded, setIntroExpanded] = useState(false);
    const [recipientsExpanded, setRecipientsExpanded] = useState(false);

    const [showSend, setShowSend] = useState(false);
    const [clients, setClients] = useState([]);
    const [selectedClientId, setSelectedClientId] = useState('');
    const [selectedContactIds, setSelectedContactIds] = useState([]);
    const [sending, setSending] = useState(false);
    const [sendResult, setSendResult] = useState('');

    useEffect(() => {
        setLoading(true);
        setError(false);
        API.getReportDetail(reportId)
            .then(res => {
                setReport(res.data);
                if (res.data.client) setSelectedClientId(res.data.client.client_id);
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [reportId]);

    const calendarHeaders = useMemo(
        () => report ? computeCalendarHeaders(report.bills, report.sort_mode) : new Map(),
        [report]
    );

    const loadClients = () => {
        if (clients.length > 0 || !teamId) return;
        API.getTeamClients(teamId)
            .then(res => setClients(res.data.filter(c => !c.archived)))
            .catch(err => console.error('Error loading clients:', err));
    };

    const toggleContact = (contactId) => {
        setSelectedContactIds(prev =>
            prev.includes(contactId) ? prev.filter(id => id !== contactId) : [...prev, contactId]
        );
    };

    const handleSend = async () => {
        if (!selectedClientId || selectedContactIds.length === 0) return;
        setSending(true);
        setSendResult('');
        try {
            await API.sendReport(report.id, {
                client_id: selectedClientId,
                contact_ids: selectedContactIds,
            });
            setSendResult('Report sent successfully.');
            setSelectedContactIds([]);
            const res = await API.getReportDetail(reportId);
            setReport(res.data);
        } catch (err) {
            setSendResult(err.response?.data?.msg || 'Failed to send report.');
        } finally {
            setSending(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(`Delete "${report.title}"? This cannot be undone.`)) return;
        setDeleting(true);
        try {
            await API.deleteReport(report.id);
            onDeleted?.();
        } catch (err) {
            alert(err.response?.data?.msg || 'Failed to delete report.');
            setDeleting(false);
        }
    };

    const selectedClient = clients.find(c => String(c.client_id) === String(selectedClientId));
    const contactsWithEmail = (selectedClient?.TeamClientContacts || []).filter(c => c.email);
    const alreadySent = new Set((report?.recipients || []).map(r => String(r.contact_id)));

    return (
        <div className="modal fade show d-block rdm-overlay" tabIndex="-1" onClick={onClose}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable rdm-dialog" onClick={e => e.stopPropagation()}>
                <div className="modal-content">
                    <div className="modal-header rdm-header">
                        <div>
                            <h5 className="modal-title rdm-title">{loading ? 'Report' : report?.title}</h5>
                            {report && (
                                <div className="rdm-subtitle">
                                    {report.client?.client_name || 'Internal only'} · {report.team_name}
                                </div>
                            )}
                        </div>
                        {!loading && !error && report && (
                            <div className="rdm-header-actions">
                                <button type="button" className="rdm-action-btn" onClick={() => downloadReportAsPDF(report, report.bills)}>
                                    <i className="bi bi-file-earmark-pdf me-1" />PDF
                                </button>
                                <button type="button" className="rdm-action-btn" onClick={() => { setShowSend(v => !v); loadClients(); }}>
                                    <i className="bi bi-send me-1" />Send to Contacts
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="modal-body rdm-body">
                        {loading ? (
                            <div className="rdm-loading">Loading…</div>
                        ) : error || !report ? (
                            <div className="rdm-error">Unable to load report.</div>
                        ) : (
                            <>
                                {report.intro_message && (
                                    <div className="rdm-intro-section">
                                        <button
                                            type="button"
                                            className="rdm-intro-toggle"
                                            onClick={() => setIntroExpanded(v => !v)}
                                        >
                                            <i className={`bi ${introExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} me-1`} />
                                            Intro Message
                                        </button>
                                        {introExpanded && (
                                            <div
                                                className="rdm-intro-message"
                                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(report.intro_message) }}
                                            />
                                        )}
                                    </div>
                                )}

                                {showSend && (
                                    <div className="rdm-send-section">
                                        <label className="rdm-field-label">Client</label>
                                        <select
                                            className="form-select form-select-sm mb-2"
                                            value={selectedClientId}
                                            disabled={!!report.client}
                                            onChange={e => { setSelectedClientId(e.target.value); setSelectedContactIds([]); }}
                                        >
                                            <option value="">Select a client…</option>
                                            {clients.map(c => (
                                                <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
                                            ))}
                                        </select>
                                        {selectedClientId && (
                                            contactsWithEmail.length === 0 ? (
                                                <p className="text-muted small fst-italic mb-0">No contacts with an email on file for this client.</p>
                                            ) : (
                                                <div className="rdm-contact-list">
                                                    {contactsWithEmail.map(c => (
                                                        <label key={c.contact_id} className="rdm-contact-row">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedContactIds.includes(c.contact_id)}
                                                                onChange={() => toggleContact(c.contact_id)}
                                                            />
                                                            <span className="rdm-contact-info">
                                                                <span className="rdm-contact-name">
                                                                    {c.first_name} {c.last_name}{c.title ? `, ${c.title}` : ''}
                                                                </span>
                                                                <span className="rdm-contact-email">{c.email}</span>
                                                            </span>
                                                            {alreadySent.has(String(c.contact_id)) && (
                                                                <span className="rdm-already-sent">Previously sent</span>
                                                            )}
                                                        </label>
                                                    ))}
                                                </div>
                                            )
                                        )}
                                        {sendResult && <p className="rdm-send-result">{sendResult}</p>}
                                        <button
                                            type="button"
                                            className="btn btn-sm rdm-send-btn mt-2"
                                            disabled={!selectedClientId || selectedContactIds.length === 0 || sending}
                                            onClick={handleSend}
                                        >
                                            {sending ? 'Sending…' : 'Send'}
                                        </button>
                                    </div>
                                )}

                                {report.recipients?.length > 0 && (
                                    <div className="rdm-recipients">
                                        <button
                                            type="button"
                                            className="rdm-intro-toggle"
                                            onClick={() => setRecipientsExpanded(v => !v)}
                                        >
                                            <i className={`bi ${recipientsExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} me-1`} />
                                            Recipients ({report.recipients.length})
                                        </button>
                                        {recipientsExpanded && (
                                            <div className="rdm-recipient-list">
                                                {report.recipients.map(r => {
                                                    const contact = r.TeamClientContact;
                                                    const expired = new Date(r.expires_at) < new Date();
                                                    return (
                                                        <div key={r.id} className="rdm-recipient-row">
                                                            <span>{contact ? `${contact.first_name} ${contact.last_name}` : 'Unknown contact'}</span>
                                                            <span className="rdm-recipient-badges">
                                                                <span className={`rdm-recipient-viewed${r.opened_at ? '' : ' rdm-recipient-viewed--not-viewed'}`}>
                                                                    {r.opened_at
                                                                        ? `Viewed ${new Date(r.opened_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`
                                                                        : 'Not viewed'}
                                                                </span>
                                                                <span className={`rdm-recipient-status${expired ? ' rdm-recipient-status--expired' : ''}`}>
                                                                    {expired ? 'Expired' : 'Active'}
                                                                </span>
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="rdm-field-label mt-3">Bills ({report.bills.length})</div>
                                <div className="rdm-bill-list">
                                    {report.bills.map(bill => (
                                        <ReportBillListItem
                                            key={bill.bill_id}
                                            bill={bill}
                                            note={bill.report_note}
                                            showDigest={report.show_digest}
                                            showAiSummary={report.show_ai_summary}
                                            linkToProfile={true}
                                            calendarInfo={calendarHeaders.get(bill.bill_id)}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                    <div className="modal-footer rdm-footer">
                        {canManage && report && (
                            <button type="button" className="btn btn-sm rdm-delete-btn me-auto" onClick={handleDelete} disabled={deleting} title="Delete Report">
                                <i className={`bi ${deleting ? 'bi-hourglass-split' : 'bi-trash3'}`} />
                            </button>
                        )}
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>Close</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
